import React, { useState, useEffect } from "react";
import CustomLayout from "./Layout"; // Import the Layout component
import banner from "../assets/banner1.png";
import laptop from "../assets/laptop.png";
import puzzle from "../assets/puzzle.png";
import question from "../assets/question.png";
import { Plus, Minus } from "lucide-react";
import linkedin from "../assets/Linked in.png";
import x from "../assets/Twitter.png";
import ig from "../assets/Instagram.png";
import ScrollButtons from "../common/ScrollButtons";
import EarlyAccessModal from "../common/EarlyAccessModal";
import ContactForm from "../common/ContactForm";
import UpdatesSignupModal from "../common/UpdatesSignupModal";
import { Toaster, toast } from "react-hot-toast";
import { isMobile } from "react-device-detect";
import { logEvent } from "firebase/analytics";
import { analytics } from "../Firebase/firebase";
//import { showNotification } from "../common/ShowNotification";

import {
  requestPermission,
  onMessageListener,
} from "../Firebase/notifications";

// FAQ Accordion Component
const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200 py-4">
      <button
        className="flex justify-between items-center w-full text-left"
        onClick={onClick}
      >
        <h3
          style={{
            fontFamily: "Inter",
            fontWeight: isOpen ? "600" : "400",
            textTransform: "capitalize",
            fontSize: "18px",
            lineHeight: "24px",
            letterSpacing: "-0.001em",
            textDecoration: "none",
            textUnderlinePosition: "from-font",
            color: "#4C4D5E",
          }}
        >
          {question}
        </h3>
        <span className="text-blue-600">
          {isOpen ? (
            <Minus className="h-5 w-5" />
          ) : (
            <Plus className="h-5 w-5" />
          )}
        </span>
      </button>
      {isOpen && (
        <div
          className="mt-4"
          style={{
            fontFamily: "Inter",
            fontWeight: "400",
            fontSize: "12px",
            lineHeight: "24px",
            letterSpacing: "-0.001em",
            color: "#4C4D5E",
            paddingLeft: "24px",
            paddingRight: "16px",
            paddingTop: "8px",
            paddingBottom: "8px",
            borderLeft: "4px solid #4C4D5E",
          }}
        >
          {answer}
        </div>
      )}
    </div>
  );
};

const LandingPage = () => {
  // State for FAQ accordions
  const [openFAQs, setOpenFAQs] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdatesModalOpen, setIsUpdatesModalOpen] = useState(false);

  const [notificationStatus, setNotificationStatus] = useState("default");
  const [notificationChecked, setNotificationChecked] = useState(false);

  // Optional: Show a notification banner if permission is 'default'
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [notification, setNotification] = useState({ title: "", body: "" });

  // Listen for foreground messages - only initialize once
  useEffect(() => {
    const unsubscribe = onMessageListener((payload) => {
      console.log("Foreground notification:", payload);

      setNotification({
        title: payload?.notification?.title || "New Notification",
        body: payload?.notification?.body || "You have a new message",
      });

      toast.success(payload?.notification?.body, {
        icon: "🔔",
        duration: Infinity, // Keep the toast open until user interacts
        position: isMobile ? "top-right" : "bottom-right", // Adjust position for mobile/desktop
        style: {
          background: "#1e293b", // Dark blue-gray background
          color: "#ffffff", // White text
          borderRadius: "8px",
          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          padding: "12px 16px",
          fontSize: "16px",
          fontWeight: "500",
          cursor: "pointer", // Make it clear that it can be interacted with
        },
        iconTheme: {
          primary: "#ffcc00", // Gold icon color
          secondary: "#1e293b", // Match background
        },
        action: {
          label: "Dismiss",
          onClick: () => toast.dismiss(), // Allow user to manually close it
        },
      });

      // toast(payload?.notification?.body, {
      //   icon: "🔔",
      //   duration: 10000,
      //   position: "top-right",
      // });

      // ✅ Log notification received in Firebase Analytics
      logEvent(analytics, "notification_received", {
        title: payload?.notification?.title,
        body: payload?.notification?.body,
      });
    });

    return () => {
      unsubscribe(); // Cleanup listener when component unmounts
    };
  }, []);

  // Check notification permission on mount

  useEffect(() => {
    if (notificationChecked) return;

    const checkNotificationPermission = async () => {
      try {
        if (!("Notification" in window)) {
          setShowNotificationBanner(false);
          return;
        }

        const permission = Notification.permission;
        setNotificationStatus(permission);

        if (permission === "default") {
          setShowNotificationBanner(true);
        } else if (permission === "granted") {
          await setupNotifications(false);
        }
      } catch (error) {
        console.error("Error checking notification permission:", error);
      }
    };

    checkNotificationPermission();
    setNotificationChecked(true);
  }, [notificationChecked]);

  const setupNotifications = async (showErrors = true) => {
    try {
      const { fcmToken, userId } = await requestPermission();

      const response = await fetch(
        `${process.env.REACT_APP_MAIN_API_Url}/api/DeviceToken`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token: fcmToken,
            userId: userId,
            appType: "ZetaComs",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save token");
      }

      setShowNotificationBanner(false);
      setNotificationStatus("granted");
      return true;
    } catch (error) {
      console.error("Error setting up notifications:", error);
      if (showErrors) {
        alert(
          "Failed to set up notifications. Please ensure you're using a supported browser and try again."
        );
      }
      return false;
    }
  };

  const handleEnableNotifications = async () => {
    try {
      const permission = await Notification.requestPermission();
      setNotificationStatus(permission);

      if (permission === "granted") {
        await setupNotifications(true);
      } else if (permission === "denied") {
        alert(
          "Notification permission denied. Please enable notifications in your browser settings to receive updates."
        );
      }
    } catch (error) {
      console.error("Error in handleEnableNotifications:", error);
      alert("Failed to enable notifications. Please try again.");
    }
  };

  const NotificationBanner = () => {
    if (notificationStatus !== "default" || !showNotificationBanner) {
      return null;
    }

    return (
      <div className="fixed bottom-4 right-4 bg-white p-4 rounded-lg shadow-lg max-w-sm z-50">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-sm font-semibold text-[#0E0444]">
              Stay Updated!
            </h3>
            <p className="text-xs text-[#4C4D5E] mt-1">
              Enable notifications to receive important updates and
              announcements about ZetaComs and ZetaFlow productivity tool.
            </p>
          </div>
          <button
            onClick={() => setShowNotificationBanner(false)}
            className="text-gray-400 hover:text-gray-500"
          >
            ×
          </button>
        </div>
        <button
          onClick={handleEnableNotifications}
          className="mt-3 w-full bg-[#0C60F2] text-white text-sm py-2 px-4 rounded-md hover:bg-[#0A4ED1]"
        >
          Enable Notifications
        </button>
      </div>
    );
  };

  const toggleFAQ = (id) => {
    setOpenFAQs((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // FAQ Data
  const faqs = [
    {
      id: 1,
      question: "What is Zetacoms?",
      answer:
        "Zetacoms is a tech company dedicated to creating innovative productivity solutions for professional organizations.",
    },
    {
      id: 2,
      question: "What is ZetaFlow?",
      answer:
        "ZetaFlow is our flagship productivity platform designed to help teams collaborate effortlessly, manage tasks, and communicate smarter.",
    },
    {
      id: 3,
      question: "When is ZetaFlow launching?",
      answer:
        "ZetaFlow is currently in development and will be launching in beta within the next 6 months.",
    },
  ];

  const callUpdate = () => {
    setIsUpdatesModalOpen(true);
    console.log("Signup for updates modal is called!");
  };

  return (
    <CustomLayout onUpdateClick={callUpdate}>
      <div className="w-full">
        {/* Hero Section */}
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          {/* Heading - Always first in both mobile and desktop */}
          <div className="w-full mb-6 md:mb-8">
            <h1
              className="text-3xl md:text-4xl lg:text-5xl text-start"
              style={{
                fontFamily: "Inter",
                fontWeight: 400,
                lineHeight: "1.2",
                letterSpacing: "-0.015em",
              }}
            >
              Empowering teams,
              <span
                className="text-blue-600 block"
                style={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  letterSpacing: "-0.015em",
                }}
              >
                Redefining Productivity.
              </span>
            </h1>
          </div>

          {/* Mobile layout: Title -> Image -> Content */}
          {isMobile ? (
            <div className="flex flex-col gap-6">
              {/* Image comes next in mobile */}
              <div className="w-full">
                <img
                  src={banner}
                  alt="ZetaComs Productivity Suite"
                  className="w-full h-auto object-cover rounded-lg"
                />
              </div>

              {/* Content card */}
              <div
                className="w-full rounded-lg p-5"
                style={{
                  backgroundColor: "#F0F6FF",
                  fontFamily: "Inter",
                }}
              >
                <p
                  className="mb-4 text-[#4C4D5E] font-semibold text-lg"
                  style={{
                    fontFamily: "Inter",
                    letterSpacing: "-0.015em",
                  }}
                >
                  Revolutionizing how professional organizations collaborate,
                  manage tasks, and drive results.
                </p>

                <p
                  className="mb-6 text-[#4C4D5E] text-sm"
                  style={{
                    fontFamily: "Inter",
                    lineHeight: "1.5",
                    letterSpacing: "-0.005em",
                  }}
                >
                  Start your journey toward smarter collaboration and seamless
                  productivity. Zetacoms is here to equip you with tools that
                  empower your team, simplify your workflow, and achieve
                  measurable results—all in one intuitive platform.
                </p>

                {/* Buttons in mobile */}
                <div className="flex flex-col gap-4">
                  <button
                    className="w-full bg-[#0C60F2] text-white py-2 px-4 rounded-md text-center font-medium text-sm"
                    style={{
                      fontFamily: "Inter",
                      letterSpacing: "-0.015em",
                    }}
                    onClick={() => setIsUpdatesModalOpen(true)}
                  >
                    Sign Up for Updates
                  </button>

                  <button
                    className="w-full text-[#000A9C] py-2 px-4 text-center font-medium text-sm"
                    style={{
                      fontFamily: "Inter",
                    }}
                  >
                    Learn about ZetaFlow
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Desktop layout: Title -> Two-column content */
            <div className="flex flex-row items-center gap-8">
              {/* Left column - Content */}
              <div className="flex-1">
                <div
                  className="flex flex-col px-8 py-6 h-full rounded-lg"
                  style={{
                    backgroundColor: "#F0F6FF",
                    fontFamily: "Inter",
                  }}
                >
                  <p
                    className="mb-5 text-[#4C4D5E] font-semibold text-xl leading-7"
                    style={{
                      fontFamily: "Inter",
                      letterSpacing: "-0.015em",
                    }}
                  >
                    <span style={{ display: "block" }}>
                      Revolutionizing how professional
                    </span>
                    <span style={{ display: "block" }}>
                      organizations collaborate, manage tasks,
                    </span>
                    <span style={{ display: "block" }}>and drive results.</span>
                  </p>

                  <p
                    className="mb-6 text-[#4C4D5E]"
                    style={{
                      fontFamily: "Inter",
                      fontSize: "15px",
                      fontWeight: 400,
                      lineHeight: "24px",
                      letterSpacing: "-0.005em",
                    }}
                  >
                    Start your journey toward smarter collaboration and seamless
                    productivity. Zetacoms is here to equip you with tools that
                    empower your team, simplify your workflow, and achieve
                    measurable results —all in one intuitive platform.
                  </p>

                  {/* Desktop buttons */}
                  <div className="flex flex-row justify-between items-center">
                    <button
                      className="bg-[#0C60F2] text-white py-2 px-5 rounded-md font-medium text-sm"
                      style={{
                        fontFamily: "Inter",
                        letterSpacing: "-0.015em",
                      }}
                      onClick={() => setIsUpdatesModalOpen(true)}
                    >
                      Sign Up for Updates
                    </button>

                    <button
                      className="text-[#000A9C] font-medium text-sm"
                      style={{
                        fontFamily: "Inter",
                      }}
                    >
                      Learn about ZetaFlow
                    </button>
                  </div>
                </div>
              </div>

              {/* Right column - Image */}
              <div className="flex-1">
                <img
                  src={banner}
                  alt="ZetaComs Productivity Suite"
                  className="w-full h-auto object-cover rounded-lg"
                />
              </div>
            </div>
          )}
        </section>

        {/* Innovation Section */}
        <section className="text-white py-16 bg-[#0C60F2]" id="about">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Title & Description */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 space-y-6 md:space-y-0">
              <h2 className="text-[#0CF2B1] text-3xl md:text-[38px] font-semibold leading-snug md:leading-[48px] tracking-tight">
                Driving innovation in productivity{" "}
                <br className="hidden md:block" /> solutions
              </h2>
              <p className="max-w-md text-sm md:text-[15.5px] leading-[24px] text-white text-justify">
                Zetacoms Tech. was born out of a desire to simplify complex
                workflows and redefine collaboration in professional
                organizations. Our mission is simple: build intuitive, AI-driven
                tools that empower businesses to unlock their full potential.
              </p>
            </div>

            {/* Cards - Our Promise & Core Values */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-justify">
              {/* Our Promise */}
              <div className="bg-[#0E0444] p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-6 text-[#F0F6FF]">
                  Our Promise
                </h3>
                <ol className="space-y-4 text-white text-sm md:text-[15px] font-semibold leading-[28px]">
                  <li>
                    1. AI-Powered Technology: Boost efficiency with smart
                    insights and automation.
                  </li>
                  <li>
                    2. Scalable Solutions: Whether you're a small team or a
                    multinational organization, we adapt to your needs.
                  </li>
                  <li>
                    3. Human-Centered Design: We prioritize simplicity,
                    usability, and effectiveness.
                  </li>
                </ol>
              </div>

              {/* Core Values */}
              <div className="bg-white p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-6 text-[#0C60F2]">
                  Core Values
                </h3>
                <ol className="space-y-4 text-[#4C4D5E] text-sm md:text-[15px] font-semibold leading-[28px]">
                  <li>
                    1. Innovation: Constantly pushing boundaries to create
                    cutting-edge tools.
                  </li>
                  <li>2. Collaboration: Enhancing teamwork at every level.</li>
                  <li>
                    3. Transparency: Building trust through integrity and
                    openness.
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* ZetaFlow Features Section laptop */}
        {/* <section className="py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-4 sm:mb-8">
            <div
              className="grid md:grid-cols-2 gap-1 md:gap-8 items-center mb-8 md:mb-8 px-4 md:px-12 rounded-md"
              style={{
                backgroundColor: "#FAF9FF",
              }}
            >
              <div>
                <img
                  src={laptop}
                  alt="ZetaFlow Dashboard"
                  className="grid md:grid-cols-2 gap-1 md:gap-8 items-center mb-8 md:mb-8 px-4 md:px-12 rounded-md"
                />
              </div>
              <div>
                <h2
                  className="font-bold mb-8 md:mb-4 mt-1 text-[#0E0444] text-left"
                  style={{
                    fontFamily: "Inter",
                    fontSize: { isMobile } ? "32px" : "38px",
                    fontWeight: 400,
                    lineHeight: "47px",
                    letterSpacing: "-0.015em",
                  }}
                >
                  {isMobile ? (
                    <>
                      Meet ZetaFlow: <br />
                      Productivity redefined
                    </>
                  ) : (
                    <>
                      Meet ZetaFlow: Productivity <br />
                      redefined
                    </>
                  )}
                </h2>
                <p
                  className={`mb-6 text-[#4C4D5E] ${
                    isMobile
                      ? "text-[16px] leading-[26px] tracking-tight"
                      : "text-[16px] leading-[26px] tracking-tight"
                  }`}
                  style={{
                    fontFamily: "Inter",
                    fontWeight: 400,
                    textAlign: "justify",
                    letterSpacing: "-0.05em",
                  }}
                >
                  {isMobile ? (
                    <span className="text-justify">
                      <span style={{ display: "block", textAlign: "justify" }}>
                        Your all-in-one productivity platform designed to help
                        teams
                      </span>
                      <span style={{ display: "block", textAlign: "justify" }}>
                        collaborate effortlessly, manage tasks, and communicate
                        smarter.
                      </span>
                      <span style={{ display: "block", textAlign: "justify" }}>
                        ZetaFlow combines cutting-edge technology with
                        user-friendly
                      </span>
                      <span style={{ display: "block", textAlign: "justify" }}>
                        features to deliver the ultimate productivity
                        experience.
                      </span>
                    </span>
                  ) : (
                    <>
                      <span style={{ display: "block" }}>
                        Your all-in-one productivity platform, designed to help
                        teams
                      </span>
                      <span style={{ display: "block" }}>
                        collaborate effortlessly, manage tasks, and communicate
                        smarter.
                      </span>
                      <span style={{ display: "block" }}>
                        ZetaFlow combines cutting-edge technology with
                        user-friendly
                      </span>
                      <span style={{ display: "block" }}>
                        features to deliver the ultimate productivity
                        experience.
                      </span>
                    </>
                  )}
                </p>
                <button
                  className="px-5 py-1.5 rounded-md font-medium mb-0"
                  style={{
                    fontFamily: "Inter",
                    backgroundColor: "#0C60F2",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "20px",
                    letterSpacing: "-0.015em",
                    color: "#FFFFFF",
                  }}
                  onClick={() => setIsModalOpen(true)}
                >
                  Sign Up for Early Access
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8 px-4 md:px-0">
              <div>
                <h3
                  className="font-semibold mb-4"
                  style={{
                    fontFamily: "Inter",
                    fontSize: "22px",
                    fontWeight: 600,
                    lineHeight: "32px",
                    letterSpacing: "-0.015em",
                    color: "#4C4D5E",
                  }}
                >
                  AI Meeting Insights
                </h3>
                <p
                  style={{
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    letterSpacing: "-0.05em",
                    color: "#4C4D5E",
                  }}
                >
                  Say goodbye to manual note-taking. ZetaFlow <br />
                  records, transcribes, and generates actionable summaries for
                  every meeting.
                </p>
              </div>
              <div>
                <h3
                  className="mb-4"
                  style={{
                    fontFamily: "Inter",
                    fontSize: "22px",
                    fontWeight: 600,
                    lineHeight: "32px",
                    letterSpacing: "-0.015em",
                    color: "#4C4D5E",
                  }}
                >
                  Smart Task Management
                </h3>
                <p
                  style={{
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    letterSpacing: "-0.05em",
                    color: "#4C4D5E",
                  }}
                >
                  Assign, track, and complete tasks effortlessly with a system
                  that prioritizes what matters.
                </p>
              </div>
              <div>
                <h3
                  className="mb-4"
                  style={{
                    fontFamily: "Inter",
                    fontSize: "22px",
                    fontWeight: 600,
                    lineHeight: "32px",
                    letterSpacing: "-0.015em",
                    color: "#4C4D5E",
                  }}
                >
                  Integrated Chat
                </h3>
                <p
                  style={{
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    letterSpacing: "-0.05em",
                    color: "#4C4D5E",
                  }}
                >
                  Collaborate in real-time with a secure and seamless chat
                  platform built right into your workflow.
                </p>
              </div>
              <div>
                <h3
                  className="mb-4"
                  style={{
                    fontFamily: "Inter",
                    fontSize: "22px",
                    fontWeight: 600,
                    lineHeight: "32px",
                    letterSpacing: "-0.015em",
                    color: "#4C4D5E",
                  }}
                >
                  Multi-Tenant Framework
                </h3>
                <p
                  style={{
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    letterSpacing: "-0.05em",
                    color: "#4C4D5E",
                  }}
                >
                  Scalable for any organization, ZetaFlow <br />
                  ensures each tenant has their own secure environment.
                </p>
              </div>
            </div>
          </div>
        </section> */}
        {/* ZetaFlow Features Section laptop - FIXED */}
        <section className="py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-4 sm:mb-8">
            <div
              className="grid md:grid-cols-2 gap-1 md:gap-8 items-center mb-8 md:mb-8 px-4 md:px-12 rounded-md"
              style={{
                backgroundColor: "#FAF9FF",
              }}
            >
              <div>
                <img
                  src={laptop}
                  alt="ZetaFlow Dashboard"
                  className="w-full h-auto"
                />
              </div>
              <div>
                <h2
                  className="font-bold mb-8 md:mb-4 mt-1 text-[#0E0444] text-left"
                  style={{
                    fontFamily: "Inter",
                    fontSize: isMobile ? "32px" : "38px",
                    fontWeight: 600, // Changed from 400 to 600 per Figma design
                    lineHeight: "47px",
                    letterSpacing: "-0.015em",
                  }}
                >
                  {isMobile ? (
                    <>
                      Meet ZetaFlow: <br />
                      Productivity redefined
                    </>
                  ) : (
                    <>
                      Meet ZetaFlow: Productivity <br />
                      redefined
                    </>
                  )}
                </h2>
                <p
                  className="mb-6 text-[#4C4D5E]"
                  style={{
                    fontFamily: "Inter",
                    fontSize: "16px", // Consistent font size
                    fontWeight: 400,
                    lineHeight: "26px",
                    letterSpacing: "-0.015em", // Changed from -0.05em to match Figma
                    textAlign: "left", // Changed from justify to left per Figma
                  }}
                >
                  {isMobile ? (
                    <>
                      Your all-in-one productivity platform designed to help
                      teams collaborate effortlessly, manage tasks, and
                      communicate smarter. ZetaFlow combines cutting-edge
                      technology with user-friendly features to deliver the
                      ultimate productivity experience.
                    </>
                  ) : (
                    <>
                      Your all-in-one productivity platform, designed to help
                      teams collaborate effortlessly, manage tasks, and
                      communicate smarter. ZetaFlow combines cutting-edge
                      technology with user-friendly features to deliver the
                      ultimate productivity experience.
                    </>
                  )}
                </p>
                <button
                  className="px-5 py-1.5 rounded-md font-medium mb-4" // Added margin-bottom for spacing
                  style={{
                    fontFamily: "Inter",
                    backgroundColor: "#0C60F2",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "20px",
                    letterSpacing: "-0.015em",
                    color: "#FFFFFF",
                  }}
                  onClick={() => setIsModalOpen(true)}
                >
                  Sign Up for Early Access
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-8 px-4 md:px-0">
              <div>
                <h3
                  className="font-semibold mb-4"
                  style={{
                    fontFamily: "Inter",
                    fontSize: "22px",
                    fontWeight: 600,
                    lineHeight: "32px",
                    letterSpacing: "-0.015em",
                    color: "#4C4D5E",
                  }}
                >
                  AI Meeting Insights
                </h3>
                <p
                  style={{
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    letterSpacing: "-0.015em", // Changed from -0.05em
                    color: "#4C4D5E",
                  }}
                >
                  Say goodbye to manual note-taking. ZetaFlow <br />
                  records, transcribes, and generates actionable summaries for
                  every meeting.
                </p>
              </div>
              <div>
                <h3
                  className="font-semibold mb-4" // Added font-semibold class
                  style={{
                    fontFamily: "Inter",
                    fontSize: "22px",
                    fontWeight: 600,
                    lineHeight: "32px",
                    letterSpacing: "-0.015em",
                    color: "#4C4D5E",
                  }}
                >
                  Smart Task Management
                </h3>
                <p
                  style={{
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    letterSpacing: "-0.015em", // Changed from -0.05em
                    color: "#4C4D5E",
                  }}
                >
                  Assign, track, and complete tasks effortlessly with a system
                  that prioritizes what matters.
                </p>
              </div>
              <div>
                <h3
                  className="font-semibold mb-4" // Added font-semibold class
                  style={{
                    fontFamily: "Inter",
                    fontSize: "22px",
                    fontWeight: 600,
                    lineHeight: "32px",
                    letterSpacing: "-0.015em",
                    color: "#4C4D5E",
                  }}
                >
                  Integrated Chat
                </h3>
                <p
                  style={{
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    letterSpacing: "-0.015em", // Changed from -0.05em
                    color: "#4C4D5E",
                  }}
                >
                  Collaborate in real-time with a secure and seamless chat
                  platform built right into your workflow.
                </p>
              </div>
              <div>
                <h3
                  className="font-semibold mb-4" // Added font-semibold class
                  style={{
                    fontFamily: "Inter",
                    fontSize: "22px",
                    fontWeight: 600,
                    lineHeight: "32px",
                    letterSpacing: "-0.015em",
                    color: "#4C4D5E",
                  }}
                >
                  Multi-Tenant Framework
                </h3>
                <p
                  style={{
                    fontFamily: "Inter",
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "24px",
                    letterSpacing: "-0.015em", // Changed from -0.05em
                    color: "#4C4D5E",
                  }}
                >
                  Scalable for any organization, ZetaFlow <br />
                  ensures each tenant has their own secure environment.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Building Future Section puzzle */}
        <section className="relative h-auto overflow-hidden">
          {/* Base background color */}
          <div className="absolute inset-0 bg-[#0E0444]"></div>

          {/* Top left gradient */}
          <div className="absolute top-0 left-0 w-96 h-96 bg-purple-950 rounded-full opacity-100 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute top-0 left-0 w-56 h-56 bg-whiteBg rounded-full opacity-30 blur-2xl -translate-x-1/2 -translate-y-1/2"></div>
          {/* Bottom right gradient */}
          <div className="absolute bottom-0 right-12 w-40 h-40 bg-purple-950 rounded-full opacity-25 blur-2xl translate-x-1/2 translate-y-1/2"></div>
          <div className="absolute bottom-0 right-12 w-56 h-56 bg-whiteBg rounded-full opacity-25 blur-2xl translate-x-1/2 translate-y-1/2"></div>

          {/* Content container */}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1">
            <div className="flex flex-col lg:flex-row items-start gap-6 mb-12 pl-10 md:pl-5">
              <div className="w-1/3 md:w-1/2">
                <img
                  src={puzzle}
                  alt="Question Mark Icon"
                  style={{
                    height: "auto",
                    objectFit: "cover",
                  }}
                />
              </div>
              <div className="space-y-5 mt-12">
                <h2 className="text-[#0cf2b1] text-2xl mb-8">
                  Building the Future Together!
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-start">
                  <div>
                    <h3
                      className="mb-2 text-xl text-[#FEFEFE]"
                      style={{
                        fontWeight: 600,
                      }}
                    >
                      6+ Months
                    </h3>
                    <p
                      className="text-[#FEFEFE] text-sm mb-3"
                      style={{
                        fontWeight: 400,
                      }}
                    >
                      to launch ZetaFlow Beta, bringing you a refined and
                      feature-packed product.
                    </p>
                  </div>
                  <div>
                    <h3
                      className="mb-2 text-xl text-[#FEFEFE]"
                      style={{
                        fontWeight: 600,
                      }}
                    >
                      100% Commitment
                    </h3>
                    <p
                      className="text-[#FEFEFE] text-sm mb-3"
                      style={{
                        fontWeight: 400,
                      }}
                    >
                      to simplifying workflows and empowering teams globally.
                    </p>
                  </div>
                  <div>
                    <h3
                      className="mb-2 text-xl text-[#FEFEFE]"
                      style={{
                        fontWeight: 600,
                      }}
                    >
                      10+ Advanced Features
                    </h3>
                    <p
                      className="text-[#FEFEFE] text-sm mb-3"
                      style={{
                        fontWeight: 400,
                      }}
                    >
                      under development to transform how organizations operate.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section
          className="py-8 bg-gray-50"
          id="faqs"
          style={{
            backgroundColor: "#FAF9FF",
          }}
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-start gap-6 mb-5 pl-10 md:pl-5">
              <div className="w-1/3 md:w-1/2">
                <img
                  src={question}
                  alt="Question Mark Icon"
                  style={{
                    height: "auto",
                    objectFit: "cover",
                  }}
                />
              </div>
              <div className="w-full lg:w-3/5">
                <h2
                  className="mb-10"
                  style={{
                    fontFamily: "Inter",
                    fontSize: "26px",
                    fontWeight: 600,
                    lineHeight: "34px",
                    letterSpacing: "-0.025em",
                    color: "#0E0444",
                    bottom: "10px",
                  }}
                >
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4 mt-4 lg:mt-0">
                  {faqs.map((faq) => (
                    <FAQItem
                      key={faq.id}
                      question={faq.question}
                      answer={faq.answer}
                      isOpen={openFAQs[faq.id]}
                      onClick={() => toggleFAQ(faq.id)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-4 bg-white" id="contact">
          <ContactForm />
        </section>

        {/* Footer */}
        <footer className="relative overflow-hidden">
          {/* Base background */}
          <div className="absolute inset-0 bg-[#0E0444]"></div>

          {/* Left gradient */}
          <div className="absolute top-0 left-0 w-96 h-96 bg-purple-950 rounded-full opacity-100 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute top-0 left-0 w-56 h-56 bg-whiteBg rounded-full opacity-30 blur-2xl -translate-x-1/2 -translate-y-1/2"></div>

          {/* Right gradient */}
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-950 rounded-full opacity-30 blur-3xl translate-x-1/2 translate-y-1/2"></div>

          {/* Content */}
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-[#FAF9FF] text-sm">
                © 2025 Zetacoms Inc. Empowering Teams, One Innovation at a Time.
              </p>

              <div className="flex items-center gap-4">
                {/* LinkedIn with proper URL */}
                <a
                  href="https://www.linkedin.com/company/zetacoms/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  aria-label="Visit Zetacoms LinkedIn"
                >
                  <img src={linkedin} alt="LinkedIn" className="w-6 h-6" />
                </a>
                {/* Twitter/X with placeholder URL */}
                <a
                  href="https://twitter.com/zetacoms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  aria-label="Visit Zetacoms Twitter"
                >
                  <img src={x} alt="Twitter" className="w-6 h-6" />
                </a>
                {/* Instagram with placeholder URL */}
                <a
                  href="https://instagram.com/zetacoms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  aria-label="Visit Zetacoms Instagram"
                >
                  <img src={ig} alt="Instagram" className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
        </footer>

        <ScrollButtons />

        <EarlyAccessModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />

        {/* Add the notification banner */}
        <NotificationBanner />

        <Toaster />

        <UpdatesSignupModal
          isOpen={isUpdatesModalOpen}
          onClose={() => setIsUpdatesModalOpen(false)}
        />
      </div>
    </CustomLayout>
  );
};

export default LandingPage;
